import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, Award, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { SHOW_READINESS } from "@/lib/hooks";

export const useExecutive = () => useQuery({ queryKey: ["executive"], queryFn: async () => (await api.get("/executive")).data, staleTime: 0 });

export const DraftBadge = ({ status, testId }) => status === "draft" ? <span data-testid={testId} className="rounded-sm border border-brand-action px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-action">Draft</span> : null;

export const SessionList = ({ sessions }) => (
  <ol className="mt-6 border-t border-border">
    {sessions.map((s) => (
      <li key={s.id}>
        <Link to={`/executive/sessions/${s.id}`} data-testid={`exec-session-link-${s.id}`} className="flex flex-col gap-2 border-b border-border py-5 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:gap-6">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center border font-heading font-bold ${s.completed ? "border-brand-accent text-brand-accent" : "border-border"}`}>{s.completed ? <Check className="h-4 w-4" /> : s.number}</span>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 font-heading text-lg font-bold">{s.title} <DraftBadge status={s.status} testId={`exec-draft-${s.id}`} /></div>
            <div className="line-clamp-2 text-sm text-muted-foreground">{s.summary}</div>
          </div>
          <div className="text-sm text-muted-foreground sm:w-60 sm:text-right"><span className="line-clamp-2">{s.decision}</span><div className="font-mono text-xs">~{s.minutes} min</div></div>
        </Link>
      </li>
    ))}
  </ol>
);

const Card = ({ to, icon: Icon, label, title, body, testId }) => (
  <Link to={to} data-testid={testId} className="border border-border bg-card p-5 transition-colors hover:bg-muted/50">
    <div className="overline-label">{label}</div>
    <div className="mt-2 flex items-center gap-2 font-heading text-lg font-bold"><Icon className="h-5 w-5 text-brand-data" />{title}</div>
    <p className="mt-1 text-sm text-muted-foreground">{body}</p>
  </Link>
);

export const ExecutiveHome = () => {
  const { user } = useAuth();
  const { data } = useExecutive();
  if (!data) return <Loader />;
  const done = data.sessions.filter((s) => s.completed).length;
  const next = data.sessions.find((s) => !s.completed);
  const best = data.attempts.reduce((m, a) => Math.max(m, a.score), 0);
  return (
    <PageShell>
      <div className="overline-label">Leader Briefing · {done} of {data.sessions.length} sessions done</div>
      <h1 data-testid="dashboard-greeting" className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">Hello, {user.name.split(" ")[0]}.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">{data.title}. {data.course_outcome}</p>
      <p data-testid="exec-doctrine" className="mt-3 max-w-2xl border-l-4 border-brand-primary pl-4 font-semibold">{data.doctrine}</p>
      <div data-testid="dashboard-next-action" className="mt-8 flex flex-col gap-5 rounded-xl border-2 border-brand-primary bg-card p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
        <div><div className="text-sm font-semibold text-brand-primary">Your next step</div><div className="mt-1 font-heading text-xl font-bold sm:text-2xl">{next ? `Session ${next.number}: ${next.title}` : data.passed ? "View your Executive certificate" : "Take the leader quiz"}</div></div>
        <Link to={next ? `/executive/sessions/${next.id}` : data.passed ? "/certificates" : "/executive/check"} data-testid="dashboard-resume-button" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">Continue <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <h2 className="mt-12 font-heading text-2xl font-bold">{data.sessions.length} sessions, one decision each</h2>
      <SessionList sessions={data.sessions} />
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <Card to="/executive/check" icon={Award} testId="exec-check-card" label="Leader quiz" title={data.passed ? `Passed · best ${best}%` : best ? `Best so far ${best}%` : "10 scenario questions"} body="10 questions drawn from all nine sessions. Pass at 8/10. Unlimited retakes." />
        <div data-testid="exec-badge-card" className={`border p-5 ${data.badge_earned ? "border-brand-accent bg-brand-accent/10" : "border-border bg-card"}`}>
          <div className="overline-label">Badge</div>
          <div className="mt-2 flex items-center gap-2 font-heading text-lg font-bold"><Award className={`h-5 w-5 ${data.badge_earned ? "text-brand-accent" : "text-muted-foreground"}`} />{data.badge}</div>
          <p className="mt-1 text-sm text-muted-foreground">{data.badge_earned ? "Earned. You finished all nine sessions and passed the leader quiz." : "Finish all nine sessions and pass the leader quiz to earn it."}</p>
        </div>
        {SHOW_READINESS && <Card to="/readiness" icon={Gauge} testId="exec-readiness-card" label="Optional" title="Readiness check" body="12 statements, about 3 minutes, private." />}
      </div>
      <p data-testid="exec-credential-note" className="mt-6 text-sm text-muted-foreground">Credential: “{data.credential_label}” certificate, verifiable online. Want more depth? Switch to the full Strategist programme from your profile menu.</p>
    </PageShell>
  );
};

export const ExecutiveLearn = () => {
  const { data } = useExecutive();
  if (!data) return <Loader />;
  return (
    <PageShell>
      <div className="overline-label">Learn · Leader Briefing</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">{data.title}</h1>
      <p className="mt-3 text-muted-foreground">{data.sessions.length} sessions of about 10 minutes. Take them in any order.</p>
      <SessionList sessions={data.sessions} />
      <Link to="/executive/check" data-testid="exec-learn-check-link" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-brand-action px-5 py-2.5 text-sm font-semibold text-brand-action-fg">Leader quiz <ArrowRight className="h-4 w-4" /></Link>
    </PageShell>
  );
};
